import { useState, useEffect } from "react";
import VotingButtons from "@/components/VotingButtons";
import { Separator } from "@/components/ui/separator";

const NewsletterWithVoting = ({ newsletter, user }) => {
  const [sections, setSections] = useState([]);

  useEffect(() => {
    if (newsletter?.content) {
      // Parse newsletter content into sections
      // Each section starts with "##" markdown heading
      const lines = newsletter.content.split('\n');
      const parsedSections = [];
      let currentSection = { id: '', title: '', content: '' };
      let sectionIndex = 0;

      lines.forEach((line, idx) => {
        if (line.trim().startsWith('##')) {
          // Save previous section if it exists
          if (currentSection.title) {
            currentSection.id = `section-${sectionIndex}`;
            parsedSections.push({ ...currentSection });
            sectionIndex++;
          }
          
          // Start new section
          currentSection = {
            id: `section-${sectionIndex}`,
            title: line.replace(/^##\s*/, '').trim(),
            content: ''
          };
        } else {
          // Add to current section content
          currentSection.content += line + '\n';
        }
      });

      // Add last section
      if (currentSection.title) {
        currentSection.id = `section-${sectionIndex}`;
        parsedSections.push(currentSection);
      }

      setSections(parsedSections);
    }
  }, [newsletter]);

  const getSectionRating = (sectionId) => {
    if (!newsletter.section_ratings || !newsletter.section_ratings[sectionId]) {
      return { upvotes: 0, downvotes: 0, userVote: null };
    }

    const rating = newsletter.section_ratings[sectionId];
    return {
      upvotes: rating.upvotes || 0,
      downvotes: rating.downvotes || 0,
      userVote: rating.user_ratings?.[user?.id] || null
    };
  };

  return (
    <div className="space-y-6">
      {sections.map((section, index) => {
        const rating = getSectionRating(section.id);
        
        return (
          <div key={section.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Section Header with Voting */}
            <div className="bg-gradient-to-r from-blue-50 to-teal-50 p-4 flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded">
                    IDEA {index + 1}
                  </span>
                  <h3 className="text-lg font-bold text-gray-900">{section.title}</h3>
                </div>
              </div>
              <div className="flex-shrink-0">
                <VotingButtons
                  newsletterId={newsletter.id}
                  sectionId={section.id}
                  initialUpvotes={rating.upvotes}
                  initialDownvotes={rating.downvotes}
                  initialUserVote={rating.userVote}
                />
              </div>
            </div>

            {/* Section Content */}
            <div className="p-6">
              <div className="prose prose-sm max-w-none">
                <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                  {section.content.trim()}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {sections.length === 0 && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
            {newsletter.content}
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsletterWithVoting;
